// src/pages/user/SubmissionEdit.jsx
// Depends on: SectionList, QuestionItem, SectionItem, QuestionList (same as FormView)
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AttachmentUploadSection from "./AttachmentUploadSection";
import SectionList from "./SectionList";
import SectionNavigator from "./SectionNavigator";
import useE2ESubmissionEditor from "../../hooks/useE2ESubmissionEditor";
import AutosaveIndicator from "../common/AutosaveIndicator";
import { useAutosave } from "../../hooks/useAutosave";
import { IDSButton, IDSButtonGroup, IDSBreadcrumbs, IDSAlert, IDSTooltip } from "@inera/ids-react";
import { formatDateTimeNoSeconds } from "../../utils/dateTime";
import { buildAnswersPayload, extractAnswersFromSections } from "../../utils/e2eAnswers";
import { getSectionStatuses } from "../../utils/e2eRequiredValidation";
import { getSubmissionStatusLabel } from "../../utils/submissionStatus";
import {
  getUserSubmission,
  submitUserSubmission,
  updateUserSubmission,
  uploadAttachment,
  getAttachments,
  deleteAttachment,
} from "../../services/E2EAPI";

export default function SubmissionEdit() {
  const { submissionId } = useParams();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const {
    answers,
    attachments,
    submitting,
    error,
    draftMessage,
    uploadError,
    setAnswers,
    setAttachments,
    setSubmitting,
    setError,
    setUploadError,
    setDraftMessage,
    validationErrors,
    handleAnswerChange,
    validateRequired,
    validateZipFile,
    markDraftSavedNow,
  } = useE2ESubmissionEditor();

  // Fetch existing submission and map its answers into the answers state
  useEffect(() => {
    async function fetchSubmission() {
      try {
        setLoading(true);
        setError("");
        setLoadError("");
        setUploadError("");

        const data = await getUserSubmission(submissionId);
        setSubmission(data);
        setAnswers(extractAnswersFromSections(data.form.sections || []));

        try {
          const existingAttachments = await getAttachments(submissionId, false);
          setAttachments(existingAttachments || []);
        } catch (attachmentErr) {
          console.error("Failed to load attachments", attachmentErr);
          setUploadError("Kunde inte ladda bilagor.");
        }
      } catch (err) {
        console.error(err);
        setLoadError("Kunde inte ladda inlämningen.");
      } finally {
        setLoading(false);
      }
    }

    if (submissionId && isAuthenticated) fetchSubmission();
  }, [submissionId, isAuthenticated]);

  // PUT answers to backend — wrapped in useCallback for stable reference in useAutosave
  const putAnswers = useCallback(async () => {
    return updateUserSubmission(
      submissionId,
      { answers: buildAnswersPayload(answers) }
    );
  }, [submissionId, answers]);

  // Autosave: fires 10 s after last answer change, only for DRAFT submissions
  const isDraft = submission?.status === 'DRAFT';
  const autosaveStatus = useAutosave(
    putAnswers,
    [answers],
    { delay: 10000, enabled: isDraft && !!submissionId }
  );

  // Live completeness per top-level section, used by the section navigator.
  const sectionStatus = useMemo(
    () => getSectionStatuses(submission?.form?.sections || [], answers),
    [submission, answers]
  );
  const missingSections = sectionStatus.filter((s) => !s.complete);

  // Save draft — type="button" bypasses form validation, stays as DRAFT
  const handleSaveDraft = async () => {
    try {
      setSubmitting(true);
      setDraftMessage("");
      setError("");

      await putAnswers();
      markDraftSavedNow();
    } catch (err) {
      console.error(err);
      setError("Kunde inte spara utkast.");
    } finally {
      setSubmitting(false);
    }
  };

  // Submit — saves answers via PUT, then marks submission as submitted
  const handleSubmit = async () => {
    if (submitting) return;

    if (!validateRequired(sections)) {
      setTimeout(() => {
        const firstError = document.querySelector('[data-question-error="true"]');
        if (firstError) firstError.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      await putAnswers();

      await submitUserSubmission(submissionId);
      navigate("/e2e/tester/submissions", {
        state: { successMessage: "Formuläret skickades in!" }
      });
    } catch (err) {
      console.error(err);
      setError("Kunde inte skicka in inlämningen.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = null;

    setUploadError("");

    if (!validateZipFile(file)) {
      return;
    }

    try {
      const uploaded = await uploadAttachment(submissionId, file);
      setAttachments((prev) => [...prev, uploaded]);
    } catch (err) {
      console.error(err);
      setUploadError("Kunde inte ladda upp filen.");
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    try {
      await deleteAttachment(submissionId, attachmentId);
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch (err) {
      console.error(err);
      setUploadError("Kunde inte ta bort bilagan.");
    }
  };

  if (loading) return <p className="ids-mt-4">Laddar inlämning…</p>;
  if (loadError) return (
    <div className="ids-m-6"><main className="ids-content ids-p-6" style={{ maxWidth: "1200px", margin: "2rem auto" }}>
      <IDSAlert type="error"><p>{loadError}</p></IDSAlert>
    </main></div>
  );
  if (!submission || !submission.form)
    return <p className="ids-mt-4">Inlämningen saknar formulärdata.</p>;

  const { status, updatedAt, form } = submission;
  const { formTitle, version, description, sections = [] } = form;

  return (
    <div className="ids-m-6">
      <main className="ids-content ids-p-6" style={{ maxWidth: "1400px", margin: "2rem auto" }}>

        <div className="ids-mb-6">
          <IDSBreadcrumbs lead="Du är här:">
            <Link to="/start">Start</Link>
            <Link to="/e2e/tester/submissions">Inlämnade E2E underlag</Link>
            <a>Redigera formulär</a>
          </IDSBreadcrumbs>
        </div>

        <h2 className="ids-heading-l ids-mb-6">Redigera formulär</h2>
        
        {/* Submission metadata */}
        <div className="ids-mb-6">
          <p className="ids-mb-2"><strong>Formulär: </strong>{formTitle} v{version}</p>
          {description && <p className="ids-mb-2">{description}</p>}
          <p className="ids-mb-2"><strong>Status: </strong>{getSubmissionStatusLabel(status)}</p>
          <p className="ids-mb-2"><strong>Senast uppdaterad: </strong>
            {updatedAt
              ? formatDateTimeNoSeconds(updatedAt)
              : "—"}
          </p>
        </div>
        

        <hr className="ids-mb-6" />

        <form>
          <div style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start" }}>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              <SectionList
                sections={sections}
                answers={answers}
                onAnswerChange={handleAnswerChange}
                validationErrors={validationErrors}
              />

              <AttachmentUploadSection
                onFileUpload={handleFileUpload}
                uploadError={uploadError}
                attachments={attachments}
                onDeleteAttachment={handleDeleteAttachment}
              />

              <IDSButtonGroup
                justify="start"
                unresponsive
              >
                <IDSButton size="m" type="button" disabled={submitting || missingSections.length > 0} onClick={handleSubmit}>
                  {submitting ? "Skickar..." : "Skicka in"}
                </IDSButton>
                <IDSButton secondary size="m" type="button" disabled={submitting} onClick={handleSaveDraft}>
                  {submitting ? "Sparar..." : "Spara utkast"}
                </IDSButton>
              </IDSButtonGroup>

              {missingSections.length > 0 && (
                <div
                  className="ids-mt-2"
                  style={{ color: "var(--ids-palette-error-40)", display: "flex", alignItems: "center", gap: "0.4rem", flexWrap: "wrap" }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}>
                    Alla obligatoriska frågor och följdfrågor måste fyllas i.
                    <IDSTooltip
                      position="top"
                      maxWidth=""
                      trigger={
                        <span
                          aria-label="Visa vilka avsnitt som saknar svar"
                          className="ids-icon-information ids-icon--m ids-icon--color-preset-1 ids-icon--interactive"
                          role="button"
                          style={{ position: "relative", zIndex: 4 }}
                          tabIndex={0}
                        />
                      }
                    >
                      <strong>Saknar svar i:</strong>
                      <ul style={{ margin: "0.3rem 0 0", paddingLeft: "1.1rem" }}>
                        {missingSections.map((s) => (
                          <li key={s.sectionId}>{s.label} ({s.missing})</li>
                        ))}
                      </ul>
                    </IDSTooltip>
                  </span>
                </div>
              )}
              <div style={{ marginTop: '0.75rem', minHeight: '1.5rem' }}>
                {/* Show autosave indicator; fall back to manual draft message when idle */}
                {autosaveStatus !== 'idle'
                  ? <AutosaveIndicator status={autosaveStatus} />
                  : draftMessage && (
                      <IDSAlert ribbon type="success">
                        {draftMessage}
                      </IDSAlert>
                    )
                }
              </div>
              {error && (
                <div className="ids-mt-4">
                  <IDSAlert type="error"><p>{error}</p></IDSAlert>
                </div>
              )}
            </div>

            <SectionNavigator sectionStatus={sectionStatus} />
          </div>
        </form>

        {/* Fixed top-right indicator — always visible regardless of scroll position */}
        <AutosaveIndicator status={autosaveStatus} fixed />

      </main>
    </div>
  );
}

